// Small, consistent building blocks used on every page.
import { useEffect, useLayoutEffect, useRef, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { STATUS, STATUS_VAR, formatMoney, formatMoneyExact, formatDate } from '../../lib/viz';
import { setHeaderSlot, clearHeaderSlot, type HeaderSlot } from '../../lib/headerSlot';
import { ActionMenu, type MenuEntry, type MenuItem } from './menu';
export { ActionMenu, PageActions, type MenuItem, type MenuEntry } from './menu';
export { SearchSelect, type SearchSelectProps } from './SearchSelect';

/**
 * A page's title, eyebrow, controls and ⋯ menu. Rendered by the shell's top bar (AppLayout), not here —
 * the page body starts straight away with its content. Pass every button as a `menu` item; `actions` is
 * for controls that are not buttons (a period picker, status badges).
 */
export function PageHeader(props: HeaderSlot) {
  const owner = useRef<symbol>(Symbol('page-header'));
  // Written on every render so a title that changes with the data stays current. Layout effects, so that on a
  // route change the leaving page clears before the arriving page writes (passive cleanups would run after).
  useLayoutEffect(() => { setHeaderSlot(props, owner.current); });
  useLayoutEffect(() => () => clearHeaderSlot(owner.current), []);
  return null;
}

export function Card({ children, className = '', title, subtitle, actions }: { children: ReactNode; className?: string; title?: string; subtitle?: string; actions?: ReactNode }) {
  return (
    <div className={`surface ${className}`}>
      {(title || actions) && (
        <div className="flex items-center justify-between gap-3 border-b border-ink-100 px-4 py-2.5">
          <div>
            {title && <h3 className="text-[13px] font-semibold text-ink-900">{title}</h3>}
            {subtitle && <p className="text-[11.5px] text-ink-500">{subtitle}</p>}
          </div>
          {actions}
        </div>
      )}
      <div className={title || actions ? 'p-4' : ''}>{children}</div>
    </div>
  );
}

/** Status pill. Colour is drawn from the reserved status palette and always paired with the label. */
const STATUS_TONE: Record<string, keyof typeof STATUS> = {
  ACTIVE: 'good', LIVE: 'good', SIGNED_UP: 'good', PAID: 'good', PUBLISHED: 'good', CLOSED: 'good', VERIFIED: 'good', PROTECTED: 'good', VALID: 'good', OPERATIONAL: 'good', COMPLETED: 'good', APPROVED: 'good', CLEARED: 'good', OCCUPIED: 'good', CONVERTED: 'good', POSTED: 'good', OPEN: 'good',
  NEW: 'neutral', DRAFT: 'neutral', DUE: 'neutral', ISSUED: 'neutral', VACANT: 'neutral', BOOKED: 'neutral', SUBMITTED: 'neutral', PENDING: 'neutral', HELD: 'neutral', SCHEDULED: 'neutral',
  TRIAGED: 'warning', ASSIGNED: 'warning', IN_PROGRESS: 'warning', NOTICE: 'warning', NOTICE_GIVEN: 'warning', PART_PAID: 'warning', PARTIAL: 'warning', EXPIRING: 'warning', RESERVED: 'warning', REFERENCING: 'warning', CONTACTED: 'warning', VIEWING_BOOKED: 'warning', AWAITING_PARTS: 'warning', ON_HOLD: 'warning', UNDER_REPAIR: 'warning', AUTO_EXECUTED: 'warning',
  OVERDUE: 'critical', EXPIRED: 'critical', FAULTY: 'critical', REJECTED: 'critical', FAILED: 'critical', CANCELLED: 'critical', VOID: 'critical', LOST: 'critical', REMEDIAL_REQUIRED: 'critical', DISPUTED: 'critical',
  EMERGENCY: 'critical', URGENT: 'serious', ROUTINE: 'neutral', PLANNED: 'good',
};
export function Badge({ value, tone }: { value: string | null | undefined; tone?: keyof typeof STATUS }) {
  if (!value) return <span className="text-ink-300">—</span>;
  const t = tone ?? STATUS_TONE[value] ?? 'neutral';
  const color = STATUS_VAR[t];
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium leading-[1.45]" style={{ color, background: `color-mix(in srgb, ${color} 12%, var(--color-white))` }}>
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: color }} />
      {value.replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase())}
    </span>
  );
}

/** `exact` always shows the pence (documents such as a VAT invoice); otherwise large amounts drop them. */
export const Money = ({ minor, currency = 'GBP', className = '', exact = false }: { minor: number | null | undefined; currency?: string; className?: string; exact?: boolean }) =>
  <span className={`tabular-nums ${className}`}>{minor === null || minor === undefined ? '—' : exact ? formatMoneyExact(minor, currency) : formatMoney(minor, currency)}</span>;

export const DateText = ({ value }: { value: string | null | undefined }) => <span>{value ? formatDate(value) : '—'}</span>;

export function Button({ children, variant = 'primary', size = 'md', icon, className = '', ...rest }: React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'accent' | 'secondary' | 'ghost' | 'danger'; size?: 'sm' | 'md'; icon?: string }) {
  const base = 'inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-full border font-medium transition disabled:opacity-50 disabled:cursor-not-allowed';
  const sizes = { sm: 'h-7 px-3 text-[12px]', md: 'h-8 px-3.5 text-[12.5px]' };
  const variants = {
    primary: 'border-bronze-500 bg-bronze-500 text-on-primary hover:border-bronze-600 hover:bg-bronze-600',
    accent: 'border-bronze-500 bg-bronze-500 text-on-primary hover:border-bronze-600 hover:bg-bronze-600',
    secondary: 'border-ink-200 bg-ink-100 text-ink-800 hover:bg-ink-200',
    ghost: 'border-transparent text-ink-500 hover:bg-ink-100 hover:text-ink-900',
    danger: 'border-red-600 bg-red-600 text-on-primary hover:border-red-700 hover:bg-red-700',
  };
  const I = icon ? (Icons as unknown as Record<string, React.ComponentType<{ className?: string }>>)[icon] : null;
  return <button data-variant={variant} className={`${base} ${sizes[size]} ${variants[variant]} ${className}`} {...rest}>{I && <I className="h-3.5 w-3.5" />}{children}</button>;
}

/**
 * One "Actions" button whose menu opens upwards (3 Nov 2026 — the lettings drawers' footer: every action the record allows
 * in one drop-up instead of a row of buttons). The same items as ActionMenu (MenuEntry: permission-gated, disabled with a
 * reason in `title`, `danger` in red, a rule above the first of them); it disappears when nothing applies.
 */
export function ActionsButton({ items, label = 'Actions' }: { items: MenuEntry[] | undefined; label?: string }) {
  // A rule above the first red item, when something sits above it (list the destructive ones last).
  const shown = (items ?? []).filter((x): x is MenuItem => !!x);
  const firstDanger = shown.findIndex((x) => x.danger);
  const menu = shown.map((x, i) => (i === firstDanger && i > 0 ? { ...x, separator: true } : x));
  return (
    <ActionMenu items={menu} up label={label} trigger={({ open, toggle }) => (
      <Button onClick={toggle} aria-haspopup="menu" aria-expanded={open}>
        {label}<Icons.ChevronUp className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </Button>
    )} />
  );
}

const CONTROL_BASE = 'rounded-lg border border-ink-200 bg-white px-2.5 text-[12.5px] text-ink-900 placeholder:text-ink-400 outline-none transition focus:border-bronze-500 focus:ring-2 focus:ring-bronze-500/30 disabled:bg-ink-50 disabled:text-ink-400';
const CONTROL = `h-8 ${CONTROL_BASE}`;

export function Input(props: React.ComponentProps<'input'>) {
  return <input {...props} className={`${CONTROL} ${props.className ?? ''}`} />;
}
export function Select(props: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...props} className={`${CONTROL} pr-8 ${props.className ?? ''}`} />;
}
export function Textarea(props: React.ComponentProps<'textarea'>) {
  return <textarea {...props} className={`${CONTROL_BASE} py-2 ${props.className ?? ''}`} />;
}
export function Field({ label, children, hint }: { label: string; children: ReactNode; hint?: string }) {
  return <label className="block"><span className="mb-1 block text-[12px] font-medium text-ink-500">{label}</span>{children}{hint && <span className="mt-1.5 block text-[11px] leading-relaxed text-ink-400">{hint}</span>}</label>;
}

/** A grey placeholder block while data loads; size it with `className` (h-4 w-24…). */
export function Skeleton({ className = '' }: { className?: string }) {
  return <span aria-hidden className={`block animate-pulse rounded bg-ink-100 ${className}`} />;
}

/** Placeholder rows for a table or a card's list while it loads. */
export function SkeletonRows({ rows = 4, cols = 4, className = '' }: { rows?: number; cols?: number; className?: string }) {
  return (
    <div role="status" aria-label="Loading" className={`space-y-3 ${className}`}>
      {Array.from({ length: rows }, (_, r) => (
        <div key={r} className="grid items-center gap-3" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
          {Array.from({ length: cols }, (_, c) => <Skeleton key={c} className={`h-3 ${c === 0 ? 'w-4/5' : c === cols - 1 ? 'ml-auto w-1/2' : 'w-2/3'}`} />)}
        </div>
      ))}
    </div>
  );
}

export function EmptyState({ icon = 'Inbox', title, body, action }: { icon?: string; title: string; body?: string; action?: ReactNode }) {
  const I = (Icons as unknown as Record<string, React.ComponentType<{ className?: string }>>)[icon] ?? Icons.Inbox;
  return (
    <div className="rounded-xl border border-ink-200 bg-white py-12 text-center">
      <div className="mx-auto grid h-10 w-10 place-items-center rounded-full bg-ink-100 text-ink-500"><I className="h-[18px] w-[18px]" /></div>
      <p className="mt-3 text-[14px] font-semibold text-ink-900">{title}</p>
      {body && <p className="mx-auto mt-1 max-w-sm text-[12.5px] text-ink-500">{body}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

/* Open sheets, innermost last, so Escape closes only the one on top. */
const sheetStack: symbol[] = [];

// lg and xl (the lettings record drawers) take half the screen (3 Nov 2026), never narrower than a form needs.
const SHEET_WIDTH = { sm: 'sm:max-w-[460px]', md: 'sm:max-w-[560px]', lg: 'sm:max-w-[max(50vw,640px)]', xl: 'sm:max-w-[max(50vw,720px)]' };

/**
 * The one dialog surface. Slides in from the right on a desktop or tablet; rises from the bottom on a phone.
 * Header · scrolling body · sticky footer. `z` lets a dialog opened from inside another sheet stack above it.
 */
export function Sheet({ open, onClose, title, eyebrow, subtitle, children, footer, footerRef, size = 'md', z = 40, padded = true, accent, headerExtra, below, raw = false, className = '', bodyClassName = '', label, hideClose = false }: {
  open: boolean; onClose: () => void; title?: ReactNode; eyebrow?: ReactNode; subtitle?: ReactNode; children: ReactNode; footer?: ReactNode;
  size?: keyof typeof SHEET_WIDTH; z?: number; padded?: boolean; accent?: string; headerExtra?: ReactNode;
  /** Rendered under the header, outside the scroll (a tab strip). */ below?: ReactNode;
  /** No header or body wrapper: the children lay out the whole panel themselves. */ raw?: boolean;
  className?: string; bodyClassName?: string; label?: string;
  /** No × in the header (the footer carries a Close button). Escape and the scrim still close it. */ hideClose?: boolean;
  /** The footer bar as a slot: content is portalled in (lettings RecordDrawer); the bar hides while it is empty. */ footerRef?: (el: HTMLDivElement | null) => void;
}) {
  const id = useRef<symbol>(Symbol('sheet'));
  useEffect(() => {
    if (!open) return;
    const me = id.current;
    sheetStack.push(me);
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && sheetStack[sheetStack.length - 1] === me) { e.stopPropagation(); onClose(); } };
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('keydown', onKey); const i = sheetStack.indexOf(me); if (i >= 0) sheetStack.splice(i, 1); };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <>
      <div className="sheet-scrim fixed inset-0 bg-ink-900/30" style={{ zIndex: z }} onClick={onClose} />
      <div role="dialog" aria-modal="true" aria-label={label ?? (typeof title === 'string' ? title : undefined)}
        className={`sheet-panel fixed inset-x-0 bottom-0 flex max-h-[92dvh] w-full flex-col overflow-hidden rounded-t-xl border border-ink-200 bg-white shadow-pop sm:inset-y-2 sm:left-auto sm:right-2 sm:max-h-none sm:w-[calc(100%-1rem)] sm:rounded-xl ${SHEET_WIDTH[size]} ${className}`} style={{ zIndex: z + 1 }}>
        <div className="mx-auto mt-2.5 h-1 w-10 shrink-0 rounded-full bg-ink-200 sm:hidden" aria-hidden />
        {raw ? children : <>
        {(title || eyebrow) && (
          <div className="flex shrink-0 items-start justify-between gap-3 border-b border-ink-100 px-4 py-3 sm:px-5" style={accent ? { boxShadow: `inset 0 3px 0 ${accent}` } : undefined}>
            <div className="min-w-0">
              {eyebrow && <p className="eyebrow mb-1">{eyebrow}</p>}
              {title && <h3 className="text-[15px] font-semibold leading-snug tracking-[-0.01em] text-ink-900">{title}</h3>}
              {subtitle && <p className="mt-0.5 text-[12px] text-ink-500">{subtitle}</p>}
            </div>
            <div className="flex shrink-0 items-center gap-1">
              {headerExtra}
              {!hideClose && <button type="button" onClick={onClose} aria-label="Close" className="grid h-8 w-8 place-items-center rounded-md text-ink-500 transition hover:bg-ink-100 hover:text-ink-900"><Icons.X className="h-4 w-4" /></button>}
            </div>
          </div>
        )}
        {below}
        <div className={`min-h-0 flex-1 overflow-y-auto ${padded ? 'px-4 py-4 sm:px-5' : ''} ${bodyClassName}`}>{children}</div>
        {(footer || footerRef) && <div ref={footerRef} className="safe-bottom flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-ink-100 bg-ink-50/60 px-4 py-2.5 empty:hidden sm:px-5">{footer}</div>}
        </>}
      </div>
    </>
  );
}

/** Kept for the fifty screens that say `<Modal>`; it is a Sheet. */
export function Modal({ size = 'md', ...rest }: { open: boolean; onClose: () => void; title: ReactNode; subtitle?: ReactNode; headerExtra?: ReactNode; hideClose?: boolean; children: ReactNode; footer?: ReactNode; size?: 'md' | 'lg'; z?: number }) {
  return <Sheet size={size} {...rest} />;
}

export function KeyValue({ items }: { items: Array<{ label: string; value: ReactNode }> }) {
  return (
    <dl className="grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2">
      {items.map((it) => (<div key={it.label}><dt className="text-[10.5px] font-semibold uppercase tracking-[0.06em] text-ink-500">{it.label}</dt><dd className="mt-1 text-[13px] text-ink-800">{it.value ?? '—'}</dd></div>))}
    </dl>
  );
}

export function BackLink({ to, label }: { to: string; label: string }) {
  return <Link to={to} className="mb-3 inline-flex items-center gap-1.5 text-[12px] font-medium text-ink-500 transition hover:text-ink-900"><Icons.ArrowLeft className="h-3.5 w-3.5" />{label}</Link>;
}

/** Underline tab strip (the admin's one tab style). `count` adds a small chip after the label. */
export function Tabs<K extends string>({ items, value, onChange, className = '' }: { items: Array<{ key: K; label: ReactNode; count?: number | null }>; value: K; onChange: (key: K) => void; className?: string }) {
  return (
    <div role="tablist" className={`flex gap-1 overflow-x-auto border-b border-ink-200 ${className}`}>
      {items.map((it) => {
        const active = it.key === value;
        return (
          <button key={it.key} type="button" role="tab" aria-selected={active} onClick={() => onChange(it.key)}
            className={`-mb-px inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap border-b-2 px-3 py-2 text-[12.5px] transition ${active ? 'border-bronze-500 font-semibold text-bronze-600' : 'border-transparent font-medium text-ink-500 hover:text-ink-900'}`}>
            {it.label}
            {it.count != null && <span className={`rounded-full px-1.5 text-[10.5px] font-semibold tabular-nums ${active ? 'bg-bronze-50 text-bronze-700' : 'bg-ink-100 text-ink-600'}`}>{it.count}</span>}
          </button>
        );
      })}
    </div>
  );
}

/** Small segmented switch (This month / This quarter, Open / Done, view switches). */
export function Segmented<K extends string>({ items, value, onChange, className = '' }: { items: Array<{ key: K; label: ReactNode; count?: number | null }>; value: K; onChange: (key: K) => void; className?: string }) {
  return (
    <div role="radiogroup" className={`inline-flex shrink-0 rounded-lg bg-ink-100 p-0.5 ${className}`}>
      {items.map((it) => {
        const active = it.key === value;
        return (
          <button key={it.key} type="button" role="radio" aria-checked={active} onClick={() => onChange(it.key)}
            className={`inline-flex h-7 items-center gap-1.5 whitespace-nowrap rounded-md px-3 text-[12px] font-medium transition ${active ? 'bg-white text-ink-900 shadow-sm' : 'text-ink-500 hover:text-ink-900'}`}>
            {it.label}
            {it.count != null && <span className="tabular-nums text-ink-400">{it.count}</span>}
          </button>
        );
      })}
    </div>
  );
}
